import {
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/**
 * Session state — Lot 1 scope.
 *
 * The access token lives in memory + `sessionStorage` (cleared on tab close,
 * never persisted to `localStorage`). It is short-lived (`ACCESS_TOKEN_TTL_MINUTES`,
 * 15 min by default) and is renewed silently through `POST /auth/refresh-token`
 * by `token-refresh.ts` — before it expires, and once on a `SESSION_EXPIRED`
 * 401 — so a merchant is no longer signed out every quarter of an hour. The
 * rotated pair comes back here through `storeRotatedTokens`.
 *
 * `activeShopId` is the tenant a merchant-scoped request acts on, sent as
 * `X-Shop-Id` (see api-client.ts). A merchant may hold memberships in
 * several tenants (the store switcher, §7.3) — this is where that selection
 * lives once the membership-listing endpoint is wired (backlog).
 */
export interface SessionState {
  readonly accessToken: string | null;
  readonly refreshToken: string | null;
  readonly activeShopId: string | null;
  /**
   * The password that opened this session is a PROVISIONAL one: the API refuses everything but the password change until the person
   * has chosen their own. Kept here only so the screen can send them straight to the change; the server decides again on every call.
   */
  readonly mustChangePassword: boolean;
}

interface SessionContextValue extends SessionState {
  readonly isAuthenticated: boolean;
  setSession: (next: {
    accessToken: string;
    refreshToken: string;
    mustChangePassword?: boolean;
  }) => void;
  setActiveShop: (shopId: string | null) => void;
  clearSession: () => void;
}

const STORAGE_KEY = 'souk-merchant-session';

const EMPTY_SESSION: SessionState = {
  accessToken: null,
  refreshToken: null,
  activeShopId: null,
  mustChangePassword: false,
};

function readStored(): SessionState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_SESSION;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'accessToken' in parsed &&
      'refreshToken' in parsed
    ) {
      const candidate = parsed as Partial<SessionState>;
      return {
        accessToken: typeof candidate.accessToken === 'string' ? candidate.accessToken : null,
        refreshToken: typeof candidate.refreshToken === 'string' ? candidate.refreshToken : null,
        activeShopId: typeof candidate.activeShopId === 'string' ? candidate.activeShopId : null,
        mustChangePassword: candidate.mustChangePassword === true,
      };
    }
  } catch {
    // Storage disabled or corrupted — degrade to "signed out", never throw.
  }
  return EMPTY_SESSION;
}

/** The session as stored for this tab (outside React: the token renewal reads it under its lock). */
export function readStoredSession(): SessionState {
  return readStored();
}

const rotationListeners = new Set<(state: SessionState) => void>();

/**
 * A renewed token pair (`token-refresh.ts`), written for this tab and pushed into `SessionProvider`. The selected
 * shop is KEPT — the same session, the same person — unlike `setSession` (a new login). Returns `false`, and
 * writes nothing, when this tab has meanwhile been signed out: a late answer must never resurrect a closed session.
 */
export function storeRotatedTokens(tokens: {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly mustChangePassword: boolean;
}): boolean {
  const current = readStored();
  if (current.accessToken === null || current.refreshToken === null) return false;
  const next: SessionState = { ...current, ...tokens };
  writeStored(next);
  for (const listener of rotationListeners) listener(next);
  return true;
}

function writeStored(state: SessionState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Session then survives for this render only — acceptable degradation.
  }
}

/**
 * DT-4 / P1-2 — purges the stored session from OUTSIDE React (`api-client.ts`
 * is a plain fetch wrapper, not a hook, and cannot call `clearSession()` on
 * `SessionProvider`'s own state). Writes the SAME empty shape `clearSession()`
 * writes, through the SAME `writeStored()`, so there is exactly one "what an
 * empty session looks like" in this file. The in-memory React state is left
 * stale on purpose: the caller (`api-client.ts`) always follows this with a
 * hard `window.location` redirect to `/login`, which remounts the whole app
 * and re-reads this now-empty value via `useState(readStored)` — the same
 * one guarantee a soft `navigate()` could not make without also threading
 * `clearSession()` through the plain fetch layer.
 */
export function clearStoredSession(): void {
  writeStored(EMPTY_SESSION);
}

/**
 * The API answered `PASSWORD_CHANGE_REQUIRED`: this session's password is a provisional one (the flag was lost - a reload, another
 * tab, a token minted before the password was reset). Records it from OUTSIDE React, like `clearStoredSession`; the caller then
 * redirects to the change screen, which remounts the app and re-reads this.
 */
export function markPasswordChangeRequired(): void {
  writeStored({ ...readStored(), mustChangePassword: true });
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): ReactElement {
  const [state, setState] = useState<SessionState>(readStored);

  // Renewed tokens (this tab's renewal, or another tab's that rotated the same session) replace the old ones in place.
  useEffect(() => {
    rotationListeners.add(setState);
    return () => {
      rotationListeners.delete(setState);
    };
  }, []);

  /**
   * `activeShopId` is deliberately RESET to `null` on every call, never
   * carried over from `prev` — a real bug found live: `sessionStorage`
   * persists across logins in the same tab, so logging in as a second,
   * different account after a first reused the first account's stale
   * `activeShopId`. `useActiveShop()`'s auto-select effect only fires
   * "if activeShopId is null", so that stale, foreign shop id survived
   * silently and every subsequent merchant-scoped request 403'd — reproduced
   * end to end (browser session held a shop id that
   * `GET /auth/me/memberships` for the newly logged-in user did not list at
   * all; confirmed via a direct API call with that exact id: "No accessible
   * shop for this request."). A new login must never inherit a shop
   * selection that might belong to someone else's memberships.
   */
  const setSession = useCallback(
    (next: { accessToken: string; refreshToken: string; mustChangePassword?: boolean }) => {
      setState((prev) => {
        const merged: SessionState = {
          ...prev,
          accessToken: next.accessToken,
          refreshToken: next.refreshToken,
          mustChangePassword: next.mustChangePassword === true,
          activeShopId: null,
        };
        writeStored(merged);
        return merged;
      });
    },
    [],
  );

  const setActiveShop = useCallback((shopId: string | null) => {
    setState((prev) => {
      const merged: SessionState = { ...prev, activeShopId: shopId };
      writeStored(merged);
      return merged;
    });
  }, []);

  const clearSession = useCallback(() => {
    writeStored(EMPTY_SESSION);
    setState(EMPTY_SESSION);
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({
      ...state,
      isAuthenticated: state.accessToken !== null,
      setSession,
      setActiveShop,
      clearSession,
    }),
    [state, setSession, setActiveShop, clearSession],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within a SessionProvider.');
  return ctx;
}
